/*
 * VoxPont Beta Access service (frontend).
 *
 * Compat SDK only — matches the rest of this repo. Do not use modular
 * firebase/firestore imports here.
 *
 * Centralizes the callables backed by functions/src/voxpontBeta and the
 * Firestore reads for beta usage/invites. Region is pinned via FUNCTIONS_REGION
 * (mirrors services/voxpontBilling.js).
 *
 * VOXPONT BETA FIRESTORE SCHEMA (Firestore creates on first write)
 * ─────────────────────────────────────────────────────────────────────────
 * voxpontBetaInvites/{inviteId}  ← doc id == invite code; written by Functions
 * voxpontBetaUsage/{uid}         ← written by Functions
 *   /sessions/{sessionId}
 * See firebase/firestore.rules + functions/src/voxpontBeta/index.js.
 * ─────────────────────────────────────────────────────────────────────────
 */
import firebaseApp, { db } from "services/firebase";
import { FUNCTIONS_REGION } from "shared/services";

// ─── CALLABLE HELPERS ────────────────────────────────────────────────

function betaCallable(name) {
  return firebaseApp.functions(FUNCTIONS_REGION).httpsCallable(name);
}

/**
 * Admin-only. Generate a new beta invite link.
 * @param {{ note?: string|null, durationDays?: number|null }} params
 * @returns {Promise<{ inviteUrl: string, inviteCode: string, expiresAt: string }>}
 */
export async function generateVoxPontBetaInvite({ note, durationDays } = {}) {
  const fn = betaCallable("generateVoxPontBetaInvite");
  const result = await fn({ note: note ?? null, durationDays: durationDays ?? null });
  return result.data;
}

/**
 * Claim a beta invite for the signed-in user.
 *
 * Consent is mandatory — the backend rejects the claim unless `consent.agreed`
 * is true, so access is never granted without a recorded agreement to the
 * Terms of Use, Privacy Policy, and BAA.
 *
 * @param {string} code
 * @param {{ agreed: boolean, documents?: string[], version?: string|null }} consent
 * @returns {Promise<{ success: true, expiresAt: string }>}
 */
export async function claimVoxPontBetaInvite(code, consent) {
  const fn = betaCallable("claimVoxPontBetaInvite");
  const result = await fn({ code, consent: consent ?? null });
  return result.data;
}

/**
 * Admin-only. Revoke an invite (immediately blocks access).
 * @param {string} code
 * @returns {Promise<{ success: true }>}
 */
export async function revokeVoxPontBetaInvite(code) {
  const fn = betaCallable("revokeVoxPontBetaInvite");
  const result = await fn({ code });
  return result.data;
}

/**
 * Record the start of a beta /voxpont4 session.
 * @returns {Promise<{ sessionId: string }>}
 */
export async function recordVoxPontBetaSessionStart() {
  const fn = betaCallable("recordVoxPontBetaSessionStart");
  const result = await fn({});
  return result.data;
}

/**
 * Record the end of a beta /voxpont4 session.
 * @param {{ sessionId: string, turns: number, functionCalls: number, durationMinutes: number }} params
 * @returns {Promise<{ success: true }>}
 */
export async function recordVoxPontBetaSessionEnd(params) {
  const fn = betaCallable("recordVoxPontBetaSessionEnd");
  const result = await fn(params);
  return result.data;
}

// ─── FIRESTORE READS ─────────────────────────────────────────────────

/**
 * Subscribe to a user's beta usage doc in real time.
 * @param {string} uid
 * @param {(usage: object|null) => void} onData
 * @returns {() => void} unsubscribe
 */
export function subscribeToVoxPontBetaUsage(uid, onData) {
  return db
    .collection("voxpontBetaUsage")
    .doc(uid)
    .onSnapshot(
      (snap) => {
        onData(snap.exists ? snap.data() : null);
      },
      (err) => {
        console.warn("[voxpontBeta] usage subscription failed", err);
        onData(null);
      }
    );
}

/**
 * Subscribe to all beta usage docs (admin dashboard) in real time.
 * @param {(rows: object[]) => void} onData
 * @returns {() => void} unsubscribe
 */
export function subscribeToAllVoxPontBetaUsage(onData) {
  return db.collection("voxpontBetaUsage").onSnapshot((snap) => {
    onData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

/**
 * Subscribe to all beta invites (admin dashboard) in real time.
 * @param {(rows: object[]) => void} onData
 * @returns {() => void} unsubscribe
 */
export function subscribeToAllVoxPontBetaInvites(onData) {
  return db.collection("voxpontBetaInvites").onSnapshot((snap) => {
    onData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  });
}

/**
 * One-shot read of a user's beta session subcollection (admin row expand).
 * @param {string} uid
 * @returns {Promise<object[]>}
 */
export async function fetchVoxPontBetaSessions(uid) {
  const snap = await db
    .collection("voxpontBetaUsage")
    .doc(uid)
    .collection("sessions")
    .orderBy("startedAt", "desc")
    .get();
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
